/**
 * Shared class strings for dropdown menus.
 *
 * Surface choice: opaque white over glass for floating menus, per design
 * call 2026-05 (see docs/css-audit.md). Trades a hair of atelier
 * consistency for legibility over a busy chat scroll — matches claude.ai,
 * Linear, Things, Raycast. The css-audit's "glass over canvas" rule (Q1)
 * applies to form/composer SURFACES, not the menus that float from them.
 */

export const MENU_CONTENT =
  "z-50 w-[280px] max-w-[calc(100vw-1rem)] rounded-xl border border-zinc-200 bg-white p-1 shadow-lg outline-none dark:border-zinc-800 dark:bg-zinc-900";

export const MENU_SUB_CONTENT =
  "z-50 w-[320px] max-w-[calc(100vw-1rem)] rounded-xl border border-zinc-200 bg-white p-1 shadow-lg outline-none dark:border-zinc-800 dark:bg-zinc-900";

export const MENU_ITEM_BASE =
  "flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-700 outline-none focus:bg-zinc-100 data-[highlighted]:bg-zinc-100 dark:text-zinc-200 dark:focus:bg-zinc-800 dark:data-[highlighted]:bg-zinc-800";

export const MENU_ITEM_DISABLED = "opacity-50 cursor-not-allowed pointer-events-none";

export const MENU_SECTION_LABEL =
  "px-2 pb-1 pt-1.5 text-xs font-medium uppercase tracking-wide text-muted";

/**
 * App chrome scroll/canvas background — settings, projects list/detail, admin
 * document pages (`DocumentPageScroll`, `AppShellMainSlotProvider` canvas).
 * Uses page `--background` (zinc greys on `/`). Do **not** use
 * `--surface-base` here — that token is editor workbench paper only
 * (`document-editor.css`, `app-shell-calm-canvas.css` on editor routes).
 */
export const APP_CHROME_CANVAS = "bg-[var(--background)]";

/**
 * Surface page layout width tiers — Interior operational pages (settings,
 * projects, workflows). `content` is the default document column; `narrow`
 * suits single-column forms; `full` uses the shell width with gutters only.
 */
export const SURFACE_PAGE_WIDTH_CONTENT =
  "mx-auto min-w-0 w-full max-w-6xl px-6";
export const SURFACE_PAGE_WIDTH_NARROW =
  "mx-auto min-w-0 w-full max-w-3xl px-6";
export const SURFACE_PAGE_WIDTH_FULL = "min-w-0 w-full px-6";

/** Mobile top clearance for the shell's floating sidebar-toggle / rail-opener band. */
export const SURFACE_PAGE_MOBILE_TOP_PAD = "pt-14 md:pt-8";

/** Interior page header typography — Granola-style inline stack (see css-audit). */
export const SURFACE_PAGE_KICKER =
  "text-xs uppercase tracking-wider text-muted";
export const SURFACE_PAGE_TITLE =
  "font-semibold tracking-tight text-zinc-900 dark:text-zinc-100 text-2xl";
export const SURFACE_PAGE_TITLE_LARGE =
  "font-semibold tracking-tight text-zinc-900 dark:text-zinc-100 text-3xl";
export const SURFACE_PAGE_DESCRIPTION = "text-sm text-muted";
export const SURFACE_PAGE_SECTION_TITLE =
  "text-lg font-semibold text-zinc-900 dark:text-zinc-100";
export const SURFACE_PAGE_META = "text-sm text-muted";

/** Pill tabs on operational surface toolbars — active = filled neutral pill. */
export const SURFACE_PAGE_TAB_ACTIVE =
  "rounded-full bg-[color-mix(in_srgb,var(--color-zinc-900)_8%,transparent)] px-3 py-1.5 text-sm font-medium text-zinc-900 dark:bg-white/10 dark:text-zinc-100";
export const SURFACE_PAGE_TAB_INACTIVE =
  "rounded-full px-3 py-1.5 text-sm text-muted transition-colors hover:text-zinc-900 dark:hover:text-zinc-100";

/** Large rounded content container on Interior catalog/grid pages. */
export const SURFACE_PAGE_CARD =
  "rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] backdrop-blur-lg";

/**
 * Golden-ratio-inspired two-column split (~62% / ~38%). Main column first,
 * aside second — pairs with `SurfacePageColumns`.
 */
export const SURFACE_PAGE_GOLDEN_COLUMNS =
  "grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.618fr)_minmax(0,1fr)]";

/**
 * Initials avatar circles (iconography tier — arbitrary px text sizes allowed).
 * Small (~20px) for assignee chips; medium (~28px) for collab header + facepile.
 * Lifted under Rule of Three: PresenceAvatar, CollabChip, FacepilePresenceAvatar.
 */
export const INITIALS_AVATAR_SM =
  "inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-semibold tracking-tight text-white";

export const INITIALS_AVATAR_MD =
  "inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold tracking-tight text-white";

/** Overlap ring for medium initials avatars in stacked facepiles / CollabChip. */
export const INITIALS_AVATAR_RING = "ring-2 ring-white dark:ring-zinc-900";

/**
 * Square 32px header icon-button (in-sidebar panel toggle / chevron). Distinct
 * from {@link FLOATING_HEADER_ICON_BUTTON} (borderless round chip for the
 * `top-3` overlay band) and from `<IconButton>` in `components/ui/IconButton.tsx`,
 * which is the 24px round ghost used for hover-revealed sidebar controls. The
 * composer's Stop/Send and the AccountMenu avatar each have their own bespoke
 * treatment and are not covered here.
 */
export const HEADER_ICON_BUTTON =
  "inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-100";

/**
 * Borderless 32px round chip for the chat main-slot header band (`top-3`
 * overlay controls — sidebar reopen, new-chat, files rail opener). Distinct
 * from {@link HEADER_ICON_BUTTON} (square `rounded-md` chrome) and from
 * `<IconButton>` (24px round ghost for hover-revealed sidebar rows).
 */
export const FLOATING_HEADER_ICON_BUTTON =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full p-0 text-zinc-500 transition-colors hover:bg-[color-mix(in_srgb,var(--color-zinc-900)_4%,transparent)] hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/[0.04] dark:hover:text-zinc-100";

/** Lucide glyph box for children of {@link FLOATING_HEADER_ICON_BUTTON}. */
export const FLOATING_HEADER_ICON = "size-4 shrink-0";

/** Locked stroke weight for header-band Lucide icons (pairs with {@link FLOATING_HEADER_ICON}). */
export const FLOATING_HEADER_ICON_STROKE = 1.6;

/** Disabled pseudo-state for composer chrome triggers (recording inertness, etc.). */
export const DISABLED_INTERACTIVE = "disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Editor toolbar select trigger (font family / size). Auto width — not the
 * fixed 32px square used by icon-only {@link HEADER_ICON_BUTTON}.
 */
export const TOOLBAR_DROPDOWN_TRIGGER =
  "inline-flex h-8 shrink-0 items-center justify-between gap-1 rounded-md px-2 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-100";

/**
 * Inline decision-surface vocabulary shared by chat-loop cards that ask
 * the user to make a one-shot choice. Today: PermissionRequestRenderer
 * (Phase A) and UserQuestionRenderer (Phase B). Matches the sidebar's
 * selected-pill language (CLAUDE.md rule 6) so the same crimson 8% tint
 * means "this surface needs your attention" everywhere in the app — but
 * only as a per-ROW accent, not as a full-card wash.
 *
 * Lift trigger: at occurrence #2 (Phase B). Theming is a cross-cutting
 * concern per AGENTS.md — the wrong-abstraction risk is near zero for
 * card chrome with a locked visual contract.
 *
 * `DECISION_CARD` is the legacy crimson-wash variant retained for
 * PermissionRequestRenderer until its own refresh. `DECISION_PANEL`
 * is the refined opaque-white floating surface — Claude Code / Raycast
 * vocabulary — used by UserQuestionRenderer after the 2026-05 refresh.
 */
export const DECISION_CARD =
  "my-2 rounded-lg border border-[var(--glass-border)] bg-[color:rgb(166_17_58_/_0.08)] p-3 backdrop-blur-xl dark:bg-[color:rgb(229_72_109_/_0.08)]";
export const DECISION_PANEL =
  "my-2 rounded-xl border border-[var(--glass-border)] bg-[var(--glass-surface-strong)] p-3 shadow-[0_8px_24px_-12px_rgba(20,24,40,0.10)] backdrop-blur-xl focus:outline-none dark:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.45)]";
export const DECISION_BTN_PRIMARY =
  "inline-flex h-7 items-center rounded-md border border-zinc-300 bg-zinc-900 px-2.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100";
export const DECISION_BTN_SECONDARY =
  "inline-flex h-7 items-center rounded-md border border-zinc-300 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-[color-mix(in_srgb,var(--color-zinc-950)_40%,transparent)] dark:text-zinc-200 dark:hover:bg-zinc-900";
export const DECISION_BTN_GHOST =
  "inline-flex h-7 w-7 items-center justify-center rounded-md border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-[color-mix(in_srgb,var(--color-zinc-950)_40%,transparent)] dark:text-zinc-200 dark:hover:bg-zinc-900";

/**
 * Apple-glass card for the agent-mediated overlay form pane (`ThreadOverlay`
 * formSlot). Same translucent-glass vocabulary as `DECISION_PANEL` (the
 * ratified GlassCard recipe — css-audit §6b: `--glass-surface-strong`
 * translucent fill + hairline `--glass-*` border + `backdrop-blur` + soft
 * shadow) but at full form-pane scale: a centered ~480px column, rounded-2xl,
 * generous padding, deeper elevation. The pane is NOT wrapped in
 * `.overlay-composer-tone`, so `--glass-surface-strong` resolves to the
 * BASE translucent token (chat-tokens) — the card reads as frosted glass on
 * the backdrop, not the composer's solid-white sheet.
 */
export const OVERLAY_FORM_CARD =
  "mx-auto w-full max-w-[480px] rounded-2xl border border-[var(--glass-border-strong)] bg-[var(--glass-surface-strong)] p-6 shadow-[0_16px_48px_-16px_rgba(20,16,12,0.30)] backdrop-blur-xl dark:shadow-[0_16px_48px_-16px_rgba(0,0,0,0.55)]";

/**
 * The app's primary/ghost action buttons at the standard h-9 size. Lifted
 * once a third consumer arrived (the `AgentCrudOverlayButton` trigger + the
 * overlay form's submit + its "Back to chat" secondary) — Rule of Three. One
 * button vocabulary across every agent-mediated CRUD surface (CLAUDE.md rule
 * 6). `BUTTON_PRIMARY` carries the disabled states so it works for gated form
 * submits.
 */
export const BUTTON_PRIMARY =
  "inline-flex h-9 items-center justify-center whitespace-nowrap gap-1.5 rounded-lg bg-zinc-900 px-3.5 text-sm font-medium text-white transition hover:bg-zinc-800 active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_60%,transparent)] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200";
export const BUTTON_GHOST =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-[var(--glass-border)] px-3.5 text-sm font-medium text-zinc-600 transition hover:bg-[color-mix(in_srgb,var(--color-zinc-900)_5%,transparent)] hover:text-zinc-900 active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_60%,transparent)] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100";
/**
 * Opaque admin-interior variant of `BUTTON_GHOST` — uses the solid
 * `border-divider` hairline instead of the glass border. Use on admin panels
 * (bulk-action rows, add buttons) where the chrome is opaque; reserve
 * `BUTTON_GHOST` for surfaces floating over the chat canvas.
 */
export const BUTTON_GHOST_ADMIN =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-divider px-3.5 text-sm font-medium text-zinc-600 transition hover:bg-[color-mix(in_srgb,var(--color-zinc-900)_5%,transparent)] hover:text-zinc-900 active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_60%,transparent)] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100";
export const BUTTON_DANGER =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-red-600 px-3.5 text-sm font-medium text-white transition hover:bg-red-700 active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-red-500)_60%,transparent)] disabled:cursor-not-allowed disabled:opacity-60 disabled:active:translate-y-0 dark:bg-red-500 dark:hover:bg-red-600";

/**
 * Divider vocabulary — a SOLID thin hairline in both modes (design call
 * 2026-06-12; reverses the 2026-06 dotted treatment — dotted read busy
 * on light data tables, and the dark zinc-700 hairline was invisible on
 * near-black cards). Color comes from ONE mode-resolved token,
 * `--color-divider` in @airis/chat-tokens/base.css (zinc-200 in light /
 * white 10% in dark), surfaced as the `border-divider` / `divide-divider`
 * utilities. Never hand-roll a divider color — consume these constants,
 * or `border-divider` directly when the shape doesn't fit (e.g.
 * `border-b … last:border-0`).
 *
 * One colour, one style, four shapes:
 * - `SECTION_DIVIDER`   — border-t between stacked sections in a card.
 * - `SECTION_DIVIDER_B` — border-b under a card's header band.
 * - `ROW_DIVIDER`       — border-t on a repeated row (`<tr>`, list row).
 * - `ROW_DIVIDERS`      — divide-y on a stack of child rows.
 */
export const SECTION_DIVIDER = "border-t border-divider";
export const SECTION_DIVIDER_B = "border-b border-divider";
export const ROW_DIVIDER = "border-t border-divider";
export const ROW_DIVIDERS = "divide-y divide-divider";

/**
 * Admin panel card chrome — rounded-lg border + opaque white/dark fill.
 * Matches AdminStatCard + ApiKeysPanel chrome. Consumers: SystemStatusPanel
 * sections, ServiceHealthPanel (if any card remains), ApiKeysPanel.
 * First lifted in D4 (fix-17-design.md §0). Edge uses the `--color-divider`
 * hairline: identical to the old zinc-200 in light, visible (white 10%)
 * in dark where zinc-800 vanished against the near-black page.
 */
export const ADMIN_CARD =
  "rounded-lg border border-divider bg-white p-4 dark:bg-zinc-950";

/**
 * Admin panel small action button — `text-xs px-2 py-1`. Used for
 * Test connection / Set / Rotate (ApiKeysPanel), Refresh all / Test now
 * (ServiceHealthPanel), Run now / Try again (SystemStatusPanel).
 * Lifted from ApiKeysPanel's local `CARD_ACTION_BUTTON` in D4; Rule of
 * Three met at 4 consumers (fix-17-design.md §5).
 */
export const ADMIN_ACTION_BUTTON =
  "rounded-md border border-divider px-2 py-1 text-xs text-zinc-700 transition hover:bg-zinc-50 active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_60%,transparent)] disabled:cursor-not-allowed disabled:opacity-60 disabled:active:translate-y-0 dark:text-zinc-300 dark:hover:bg-zinc-900";

/**
 * Admin bulk-action row — left status/count line, right action cluster.
 * Consumed by Skills, AI Models, Connectors, Signup, Users, etc.
 */
export const ADMIN_BULK_ACTION_ROW =
  "flex flex-wrap items-center justify-between gap-3";

/**
 * Uppercase kicker label over a content band — the code-block header's
 * language tag is the canonical case. Lifted under the Rule of Three
 * (MarkdownText, SharedMarkdown, ArtifactCodeView shared the identical
 * string). `text-xs` per the ratified type-scale verdict (css-audit §4:
 * Tailwind defaults only, text-xs minimum).
 */
export const SECTION_LABEL =
  "text-xs uppercase tracking-wide text-muted";

/**
 * Form-control surface — the ONE recipe for input / textarea / select
 * fields (design call 2026-06-12, same pass as `--color-divider`).
 * Fill + focus border come from the mode-resolved tokens in
 * @airis/chat-tokens/base.css: `bg-input` is solid white in light /
 * white 5% in dark (the remapped dark zinc-900/950 fills read as black
 * holes against the near-black page), and `focus:border-input-focus`
 * is zinc-400 in light / white 30% in dark. Resting border is the
 * shared divider hairline. Compose size/spacing overrides via `cn`
 * (e.g. `px-2.5 py-1.5`, `rounded-md`) — never reintroduce a
 * `dark:bg-zinc-9xx` fill or `dark:border-zinc-7xx/8xx` border on a
 * field.
 */
export const FIELD_INPUT =
  "rounded-lg border border-divider bg-input px-3 py-2 text-sm outline-none focus:border-input-focus disabled:opacity-60";

/**
 * Neutral interior callout — muted zinc fill + divider hairline for
 * settings/admin consequence panels and read-only context blocks. Not
 * semantic warning/danger (those use `glassWashClass`). Lifted at
 * occurrence #3 (ProjectAccessPanel, EmbeddingProviderPanel,
 * settings connector admin-disabled notice).
 */
export const INTERIOR_CALLOUT =
  "rounded-md border border-divider bg-zinc-50 p-3 dark:bg-[color-mix(in_srgb,var(--color-zinc-900)_40%,transparent)]";

/**
 * Glass-context field variant for forms that sit on the chat canvas /
 * overlay glass (agent-mediated CRUD forms). Border + fill resolve from
 * `--glass-border-strong` / `--glass-surface-strong` in both themes
 * (chat-tokens/base.css). Passed as a `cn` override on top of a field's
 * base classes.
 */
export const GLASS_INPUT =
  "border-[var(--glass-border-strong)] bg-[var(--glass-surface-strong)]";

/**
 * Reasoning / in-flight label shimmer — gradient text fill animated by
 * `reasoning-shimmer` (globals.css). Shared by `ShimmerText`, the chat
 * `ActivityLiveIndicator` "Thinking…" line, and office-preview loading.
 * Compose layout classes (`truncate`, `text-sm`, …) at the call site.
 */
export const REASONING_SHIMMER_TEXT =
  "[background-image:linear-gradient(90deg,var(--color-zinc-400)_0%,var(--color-zinc-900)_50%,var(--color-zinc-400)_100%)] dark:[background-image:linear-gradient(90deg,var(--color-zinc-500)_0%,var(--color-zinc-100)_50%,var(--color-zinc-500)_100%)] [background-size:200%_100%] bg-clip-text text-transparent animate-[reasoning-shimmer_2s_linear_infinite]";

/**
 * Glass card vocabulary — shared chrome for summary tiles, dialogs, and
 * canvas-floating surfaces. Semantic status is a corner gradient *wash*
 * layered over the frosted fill, never a colored border swap. Authoritative
 * spec: `docs/design-philosophy.md` (Glass vocabulary + principle 7).
 *
 * Reference implementation: `AdminStatCard` on `/admin`.
 */
export const GLASS_CARD_SHELL =
  "rounded-2xl border border-[var(--glass-border)] backdrop-blur-lg";

/** Dashboard / nav stat tile — shell + default frost + hover lift. */
export const GLASS_CARD_TILE =
  `${GLASS_CARD_SHELL} bg-[var(--glass-surface)] shadow-sm transition-colors hover:bg-[var(--glass-surface-strong)]`;

export type GlassWashTone = "default" | "warning" | "danger" | "info";

/** Corner wash — amber / degraded / needs review. */
export const GLASS_WASH_WARNING =
  "bg-gradient-to-br from-[color-mix(in_srgb,var(--color-amber-500)_10%,transparent)] via-transparent to-transparent dark:from-[color-mix(in_srgb,var(--color-amber-500)_20%,transparent)]";

/** Corner wash — down / error / attention required. */
export const GLASS_WASH_DANGER =
  "bg-gradient-to-br from-[color-mix(in_srgb,var(--color-red-500)_10%,transparent)] via-transparent to-transparent dark:from-[color-mix(in_srgb,var(--color-red-500)_20%,transparent)]";

/** Corner wash — informational callout on glass or opaque admin cards. */
export const GLASS_WASH_INFO =
  "bg-gradient-to-br from-blue-500/10 via-transparent to-transparent dark:from-blue-500/20";

/** Maps a semantic tone to the canonical wash class string (empty for default). */
export function glassWashClass(tone: GlassWashTone = "default"): string {
  switch (tone) {
    case "warning":
      return GLASS_WASH_WARNING;
    case "danger":
      return GLASS_WASH_DANGER;
    case "info":
      return GLASS_WASH_INFO;
    default:
      return "";
  }
}

/**
 * Auth canvas — Fiber paper (design-philosophy · auth gallery).
 * Warm ivory substrate + hairline laid lines + whisper grain. Form fields
 * sit *on* the paper (no floating glass card). Shell: `AuthSplitShell`.
 */
export const AUTH_FIBER_PAGE =
  "relative min-h-dvh w-full bg-[#f7f3ec] text-zinc-900 dark:bg-[#161514] dark:text-zinc-50";

export const AUTH_FIBER_WARM =
  "bg-gradient-to-b from-[#faf8f4] via-[#f5efe6] to-[#f7f3ec] dark:from-[#1a1917] dark:via-[#161514] dark:to-[#161514]";

export const AUTH_FIBER_LINES =
  "[background-image:repeating-linear-gradient(178deg,transparent,transparent_5px,rgba(90,70,50,0.014)_5px,rgba(90,70,50,0.014)_6px)] dark:[background-image:repeating-linear-gradient(178deg,transparent,transparent_5px,rgba(255,255,255,0.012)_5px,rgba(255,255,255,0.012)_6px)]";

/** SVG fractal noise — keep opacity low at the call site. */
export const AUTH_FIBER_GRAIN =
  "bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22n%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%222%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/%3E%3C/svg%3E')] bg-[length:280px_280px] mix-blend-multiply dark:mix-blend-soft-light";

/**
 * Hairline inputs that read as marks on the paper — no boxed FIELD_INPUT chrome.
 * Auth-only; settings forms keep FIELD_INPUT.
 */
export const AUTH_FIBER_INPUT =
  "w-full rounded-none border-0 border-b border-[color-mix(in_srgb,var(--color-divider)_45%,transparent)] bg-transparent px-0 py-1.5 text-sm shadow-none outline-none transition-[border-color] placeholder:text-[color-mix(in_srgb,var(--color-zinc-400)_60%,transparent)] focus:border-[color-mix(in_srgb,var(--color-zinc-500)_70%,transparent)] focus:ring-0 dark:border-white/10 dark:placeholder:text-[color-mix(in_srgb,var(--color-zinc-500)_70%,transparent)] dark:focus:border-[color-mix(in_srgb,var(--color-zinc-400)_80%,transparent)]";

export const AUTH_FIBER_LABEL =
  "text-[0.6875rem] font-medium uppercase tracking-wide text-muted";

/**
 * Shared narrow width for the auth column — login-toggle-arc. Narrower than
 * the prior `max-w-sm` (24rem) for a more geometric, elegant column; shared
 * by `/login` and `/signup` so both surfaces match exactly.
 */
export const AUTH_FIBER_WIDTH = "max-w-[19rem]";

/** Shared composer card shell — glass tokens in both themes (css-audit §6b). */
export const COMPOSER_CARD_SHELL =
  "relative flex w-full flex-col rounded-3xl border border-[var(--glass-border)] bg-[var(--glass-surface-strong)] backdrop-blur-lg shadow-[0_8px_24px_-12px_rgba(20,24,40,0.08)] transition-shadow duration-200 focus-within:shadow-[0_12px_32px_-12px_rgba(20,24,40,0.12)] focus-within:ring-1 focus-within:ring-accent-500/20";

export const COMPOSER_CHROME_TRIGGER =
  "inline-flex h-8 min-w-0 shrink items-center gap-1.5 rounded-md px-2 text-sm font-medium tracking-tight text-zinc-700 transition-colors hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 data-[state=open]:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800/60 dark:focus-visible:ring-zinc-700 dark:data-[state=open]:bg-zinc-800/60 @max-md:w-8 @max-md:shrink-0 @max-md:justify-center @max-md:gap-0 @max-md:px-0";

export const COMPOSER_CHROME_TRIGGER_LABEL = "@max-md:hidden";

export const COMPOSER_CHROME_TRIGGER_SLOT =
  "min-w-[88px] shrink @max-md:min-w-8 @max-md:shrink-0";

/**
 * Crimson 8% accent-tint row hover — matches the sidebar active-pill
 * vocabulary (css-audit §1: token-based tint via `--color-accent-600`,
 * not a raw literal; flips per mode automatically). Compose onto a
 * row/header's own layout classes at the call site (folder header,
 * library-item hover rail).
 */
export const ROW_HOVER_ACCENT =
  "hover:bg-[color-mix(in_srgb,var(--color-accent-600)_8%,transparent)]";

/**
 * Same crimson 8% tint as `ROW_HOVER_ACCENT`, applied to a dropdown item's
 * `focus:` + `data-[highlighted]:` states (destructive/delete menu items).
 * Compose on top of `MENU_ITEM_BASE` alongside `text-accent-700`.
 */
export const MENU_ITEM_ACCENT_ACTIVE =
  "focus:bg-[color-mix(in_srgb,var(--color-accent-600)_8%,transparent)] data-[highlighted]:bg-[color-mix(in_srgb,var(--color-accent-600)_8%,transparent)]";

/**
 * Full destructive dropdown-item chrome for call sites that predate
 * `MENU_ITEM_BASE` and don't compose it (no highlighted/dark variants —
 * delete-thread / delete-project rows only).
 */
export const MENU_ITEM_DESTRUCTIVE =
  "flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm text-accent-700 outline-none focus:bg-[color-mix(in_srgb,var(--color-accent-600)_8%,transparent)]";

/**
 * Coarse-pointer (touch) always-visible override — hover-revealed controls
 * default to `opacity-0` behind `group-hover`/`focus-within`, but touch
 * devices have no hover state, so this forces them visible on any
 * `pointer: coarse` input. Lifted under the Rule of Three (mobile-UI arc):
 * `InlinePreviewMenu`, `PreviewChrome`, `ThreadHistorySidebar` row controls.
 */
export const TOUCH_ALWAYS_VISIBLE = "[@media(pointer:coarse)]:opacity-100";
